import { html } from "lit";
import { StyleInfo, styleMap } from "lit/directives/style-map.js";
import { style } from "./dashboardItem.style";
import { customElement, property, query, state } from "lit/decorators.js";
import { BaseVisualization, ReportEvent } from "./baseVisualization";
import { ComponentFactory, ComponentId, Layout, MenuEvent, MenuItem, OmegaComponent, dom, actions, utils } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { MultiContextData } from "@reporting/rp-types/analytics";
import { DashboardVisualization } from "./dashboard";
import { DashboardItemSource, DashboardItemStyle } from "./dashboardAdapter";

const DEFAULT_ITEM_STYLE: DashboardItemStyle = {
	header: true,
	fgColor: 'var(--og-background-color)',
	border: {
		width: 1,
		color: 'var(--og-border-color)',
		radius: 4,
		style: 'solid'
	}
};

const formatColor = (color: string) => /^[0-9A-Fa-f]{6}$/.test(color) ? `#${color}` : color;

// border: '1px solid var(--og-border-color)',
// borderRadius: 'var(--og-base-radius)',
// backgroundColor: 'var(--og-background-color)'

@customElement('rp-visual-dashboard-item')
export class DashboardVisualizationItem extends OmegaComponent {

	static styles = [BaseVisualization.styles, style];

	componentId: ComponentId;

	@property()
	createComponent: ComponentFactory;

	@query('.body')
	body: HTMLDivElement;

	@state()
	menuItems: MenuItem[] = this.createMenuItems();

	@property({type: Object})
	component: HTMLElement;

	@property({type: Object})
	dashboard: DashboardVisualization;

	@property({type: Object})
	item: DashboardItemSource;

	@property({type: String})
	caption: string;

	@property({type: Boolean, reflect: true})
	highlight = false;

	@property({type: Boolean, reflect: true})
	transparent = false;

	private _visualization: BaseVisualization;
	get visualization(): BaseVisualization {
		// fall back to the inner report container's visualization - not every
		// visualization type dispatches report.load (e.g. gauge fires it only
		// on a chartData change), and the subeditors need the live instance
		return this._visualization
			?? (this.component as {visualization?: BaseVisualization})?.visualization;
	}

	createMenuItems() {
		return this.item?.menu ?? [];
		// const items: MenuItem[] = [];
		// items.push({k: 'properties', v: msg('Properties'), icon: 'edit'});
		// items.push({k: 'filter', v: msg('Filter'), icon: 'filter'});
		
		// if (this.visualization?.itemCount > 1) {
		// 	items.push({k: 'split', v: msg('Split'), icon: 'columns-3'});
		// }
		// items.push({k: 'remove', v: msg('Remove'), icon: 'trash', color: 'red'});
		// return items;
	}

	layout() {
		const c = this.component as Layout;
		if (c) c.layout();
	}

	/** default item style defined on the dashboard level */
	get inheritedStyle(): DashboardItemStyle {
		return ((this.dashboard?.contextData as MultiContextData)?.view_options?.item_style ?? {}) as DashboardItemStyle;
	}

	/** defaults <- dashboard item_style <- own item style */
	get mergedStyle(): DashboardItemStyle {
		return utils.mergeDeep({}, DEFAULT_ITEM_STYLE, this.inheritedStyle, this.item?.style || {});
	}

	willUpdate(props: Map<string, any>) {
		if (props.has('item')) {
			this.caption = this.item?.title ?? this.caption;
		}
		this.transparent = !!this.mergedStyle.transparent;
	}

	constructor() {
		super();
		this.addEventListener('report.load', (e: ReportEvent) => {
			this._visualization = e.visualization;
			this.caption ??= this._visualization?.caption;
			this.menuItems = this.createMenuItems();
		});
	}

	async updated() {
		// the box shadow lives on the og-dashboard-item host (inner shadow would
		// be clipped by its overflow), override it inline for transparent items
		const host = (this.getRootNode() as ShadowRoot)?.host as HTMLElement;
		if (host) host.style.boxShadow = this.transparent ? 'none' : '';

		if (!this.createComponent || this.component) {
			if (this.component) this.layout();
			return;
		}

		this.component = (await this.createComponent()) ?? dom.createElement('div');
	}

	refresh() {
		this.component = null; // force re-creation
	}

	_onMenuSelect = (e: MenuEvent) => {
		actions.dispatchActionEvent(this, 'dashboard', {
			key: e.item.k.toString(),
			value: this.item
		});
	}

	get hasHeader() {
		if (this.transparent) return false;
		return this.mergedStyle.header ?? true;
	}

	get itemStyle() : StyleInfo {
		if (this.transparent) {
			return {
				border: 'none',
				backgroundColor: 'transparent',
			};
		}

		const itemStyle = this.mergedStyle;
		const style: StyleInfo = {
			border: `${itemStyle.border.width}px ${itemStyle.border.style} ${formatColor(itemStyle.border.color)}`,
			borderRadius: `${itemStyle.border.radius}px`,
			backgroundColor: formatColor(itemStyle.fgColor),
			overflow: 'hidden'
		};
		return style;
	}

	render = () => html`
		<div class="container" style="${styleMap(this.itemStyle)}">
			${this.hasHeader ? html`
				<div class="controls ${this.caption ? 'has-title' : ''}">
					${this.caption ? html`
						<div class="title">${this.caption}</div>
					` : ''}
					
					${this.item?.menu?.length ? html`
						<og-dropdown-menu
							noCaret
							dynamicWidth
							alignment="right"
							style="width: 30px;"
							color="transparent"
							icon="ellipsis"
							@menu.select="${this._onMenuSelect}"
							.items="${this.menuItems}">
						</og-dropdown-menu>
					` : ''}
				</div>
			` : ``}

			<div class="body">
				${this.component ? this.component : html`
					<og-alert type="warning" .text="${msg('empty')}"></og-alert>
				`}
			</div>
		</div>
	`;

}
